import { useState, useEffect } from 'react';
import axios from 'axios';

import { LiaSpinnerSolid } from "react-icons/lia";
import { FaRegFolder } from "react-icons/fa6";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { IoIosCloudDone } from 'react-icons/io';
import { MdCancel } from 'react-icons/md';
import { TiTick } from 'react-icons/ti';
import { RxCrossCircled } from 'react-icons/rx';
import { AiOutlineFileDone } from 'react-icons/ai';

import ApproveQAToasterComponent from '../consultanttempatefillIncomponents/QAToasters/ApproveQAToaster';
import RejectQAToasterComponent from '../consultanttempatefillIncomponents/QAToasters/RejectQAToaster';

const TemplateA = ({ 
  templates,
  isLoading,
  selectedSubmissionId,
  onRefreshTemplates,
  showToaster,
  setShowToaster,
  setErrorMessage
}) => {

  // Filter templates for Section A (exclude manual medical certificates) - Only used to get compliance_template_submission_ids
  const regularTemplates = templates.filter(template => 
    !(template.submission_type === 'Manual' && template.template_reference === "Medical Certificate")
  );

  // ----------------- State Management -----------------
  
  // State Management - Toasters
  const [showApproveToaster, setShowApproveToaster] = useState(false);
  const [showRejectToaster, setShowRejectToaster] = useState(false);
  const [selectedDocumentForQA, setSelectedDocumentForQA] = useState(null);

  // Template document state
  const [templateDocuments, setTemplateDocuments] = useState({});
  const [loadingTemplateDocument, setLoadingTemplateDocument] = useState(false);
  const [viewingTemplateDocument, setViewingTemplateDocument] = useState(null);
  const [fullScreenImage, setFullScreenImage] = useState(null);

  // NEW: Only store and render from related documents
  const [relatedTemplateDocuments, setRelatedTemplateDocuments] = useState([]);
  const [loadingRelatedDocs, setLoadingRelatedDocs] = useState(true);

  /*-----------------------------------------------------------------
                            Use Effects 
  ---------------------------------------------------------------*/

  useEffect(() => {
    const timers = Object.keys(showToaster).map(key => 
      showToaster[key] && setTimeout(() => setShowToaster(prev => ({ ...prev, [key]: false })), 3000)
    );
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [showToaster]);

  // Fetch related documents when templates change - ONLY fetch from new API
  useEffect(() => {
    if (regularTemplates.length > 0) {
      setLoadingRelatedDocs(true);
      fetchAllRelatedDocuments();
    }
  }, [templates]);

  /*----------------------------------------------------
                      Functions
  ------------------------------------------------------*/

  // Fetch all related documents - ONLY use new API
  const fetchAllRelatedDocuments = async () => {
    try {
      const allRelatedDocs = [];
      
      for (const template of regularTemplates) {
        if (template.compliance_template_submission_id) {
          const relatedDoc = await fetchRelatedTemplateDocuments(template.compliance_template_submission_id);
          if (relatedDoc) {
            allRelatedDocs.push(relatedDoc);
          }
        }
      }
      
      setRelatedTemplateDocuments(allRelatedDocs);
      setLoadingRelatedDocs(false);
      
    } catch (error) {
      console.error('Error fetching all related documents:', error);
      setRelatedTemplateDocuments([]);
      setLoadingRelatedDocs(false);
    }
  };

  // Your new function - ONLY source of data
  const fetchRelatedTemplateDocuments = async (complianceTemplateSubmissionId) => {
    try {
      const response = await axios.get(
        `/api/compliance-tracker/quality-asssurance/related-template-responses/${complianceTemplateSubmissionId}`
      );
      
      const relatedDocs = response.data;
      
      // Return the document to use for rendering
      if (relatedDocs && relatedDocs.length > 0) {
        return relatedDocs[0]; // Return the latest document
      }
      
      return null; // No related document found
      
    } catch (error) {
      console.error('Error fetching related template responses:', error);
      return null;
    }
  };

  // ------------------ Functions Approve and Reject Toasters -----------------

  const handleApproveClick = (document) => {
    setSelectedDocumentForQA(document);
    setShowApproveToaster(true);
  };

  const handleRejectClick = (document) => {
    setSelectedDocumentForQA(document);
    setShowRejectToaster(true);
  };

  const handleApproveClose = () => {
    setShowApproveToaster(false);
    setSelectedDocumentForQA(null);
  };

  const handleRejectClose = () => {
    setShowRejectToaster(false);
    setSelectedDocumentForQA(null);
  };

  /*---------------------- Functions Approve and Reject - Save and Reject -----------------------*/

  // Function Template Reject - Rejects the consultant template submissions
  const handleRejectTemplateSubmission = async (reason) => {
    try {
      const response = await axios.post(`/api/compliance-tracker/quality-asssurance/review-consultant-submissions/template/reject/${selectedSubmissionId}`, {
        templateResponseId: selectedDocumentForQA?.template_response_id,
        qa_comment: reason
      });

      if (response.data.success) {
        console.log('Document rejected successfully');
        
        // Show success message
        setShowToaster(prev => ({ ...prev, documentRejected: true }));

        setTimeout(() => handleRejectClose(), 3000);
        onRefreshTemplates();

        // Return success (this is important for the toaster)
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error rejecting document:', error);
      setErrorMessage('Failed to reject document. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);

      // Return rejected promise so toaster can catch it
      return Promise.reject(error);
    }
  };

  // Function Template Approve - Approve the consultant template submissions
  const handleTemplateApproveSubmission = async () => {
    try {
      const response = await axios.post(`/api/compliance-tracker/quality-asssurance/review-consultant-submissions/template/approve/${selectedSubmissionId}`, {
        templateResponseId: selectedDocumentForQA?.template_response_id
      });

      if (response.data.success) {
        console.log('Document Approved successfully');
        
        // Show success message
        setShowToaster(prev => ({ ...prev, documentApproved: true }));

        // Close the approve toaster after 3 seconds
        setTimeout(() => handleApproveClose(), 3000);

        // Refresh the templates to get latest data
        onRefreshTemplates();

        // Return success (this is important for the toaster)
        return Promise.resolve();
      }
    } catch (error) {
      console.error('Error approving document:', error);
      setErrorMessage('Failed to approve document. Please try again.');
      setTimeout(() => setErrorMessage(''), 3000);
      
      // Return rejected promise so toaster can catch it
      return Promise.reject(error);
    }
  };

  // ----------------- Template Document Functions ----------------

const handleViewTemplateDocument = async (complianceTemplateSubmissionId) => {
  try {
    setLoadingTemplateDocument(true);
    setFullScreenImage(null);
    
    // Check if we already have the document cached
    if (templateDocuments[complianceTemplateSubmissionId]) {
      setViewingTemplateDocument(templateDocuments[complianceTemplateSubmissionId]);
      setLoadingTemplateDocument(false);
      return;
    }
    
    // Fetch the document from the new API endpoint
    const response = await axios.get(
      `/api/compliance-tracker/quality-asssurance/related-template-responses/${complianceTemplateSubmissionId}`
    );
    
    console.log("API Response:", response.data);
    
    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
      const documentData = response.data[0];
      console.log("Document data:", documentData);
      
      setTemplateDocuments(prev => ({
        ...prev,
        [complianceTemplateSubmissionId]: documentData
      }));
      setViewingTemplateDocument(documentData);
    } else {
      setErrorMessage("No document found for this template.");
      setTimeout(() => setErrorMessage(''), 3000);
    }
    setLoadingTemplateDocument(false);
  } catch (error) {
    console.error('Error fetching template document:', error);
    setErrorMessage('Failed to load template document. Please try again.');
    setLoadingTemplateDocument(false);
  }
};

  const closeTemplateDocumentViewer = () => {
    setViewingTemplateDocument(null);
    setFullScreenImage(null);
  };

  // Image preview handling
  const openFullScreenImage = (imageUrl) => {
    setFullScreenImage(imageUrl);
  };

  const closeFullScreenImage = () => {
    setFullScreenImage(null);
  };

  /*----------------------------------------------------------
                        Document Rendering 
  ------------------------------------------------------------*/
  const bufferToBase64 = (buffer) => {
    if (buffer && buffer.type === 'Buffer' && Array.isArray(buffer.data)) {
      const binaryString = buffer.data.map(byte => String.fromCharCode(byte)).join('');
      return btoa(binaryString);
    }
    return null;
  };
    
  const renderTemplateDocument = (document) => {
    // Check if file_name exists, if not, return Not Applicable
    if (!document.file_name) {
      return <p>Not Applicable</p>;
    }

    // Check if the file is a PDF
    const isPdf = document.file_name.toLowerCase().endsWith('.pdf');
    
    if (isPdf) {
      const base64String = bufferToBase64(document.file_response);
      if (!base64String) {
        return <p>Invalid PDF data</p>;
      }

      const base64ToBinary = atob(base64String);
      
      return (
        <div className="pdf-container">
          <BsFileEarmarkPdf
            className='view-checklist-view-image-icon'
            onClick={() => {
              // Create a new window
              const pdfWindow = window.open('', '_blank');
              
              // Write the PDF content directly to the new window
              pdfWindow.document.write(
                `<html>
                  <head>
                    <title>${document.file_name}</title>
                  </head>
                  <body style="margin: 0;">
                    <iframe
                       src="data:application/pdf;base64,${base64ToBinary}"
                       width="100%"
                       height="100%"
                       style="border: none;"
                    ></iframe>
                  </body>
                </html>`
              );
              pdfWindow.document.close();
            }}
            style={{ cursor: 'pointer' }}
          />
          Click to View PDF Document
        </div>
      );
    }

    // For non-PDF files (images)
    const fileResponse = document.file_response;
    const base64String = bufferToBase64(fileResponse);
    
    if (!base64String) {
      console.log("Failed to convert buffer to base64");
      return <p>Could not process file</p>;
    }

    try {
      const base64ToBinary = atob(base64String);
      const mimeType = 'image/png';
    
      return (
        <div>
          <img 
            src={`data:${mimeType};base64,${base64ToBinary}`}
            alt="Document Preview"
            className="document-thumbnail"
            onClick={() => openFullScreenImage(`data:${mimeType};base64,${base64ToBinary}`)} 
          />
        </div>
      );
    } catch (error) {
      console.error("Error processing file:", error);
      return <p>Error displaying file</p>;
    }
  };

  return (
    <>
      {/* Section A Header */}
      <div className='complaince-tracker-employees-medical-upload-main-text-details-section-a-container'>
        <div>
          <h3>Section A - <span className="secondary-color-paragraph">Templates</span></h3>
        </div>
      </div>

      {/* Section A - Templates Table - ONLY render from new API data */}
      {loadingRelatedDocs ? (
        <div className="loading-spinner-container">
          <LiaSpinnerSolid className="lia-spin-solid-icon" />
        </div>
      ) : (
        <div className='compliance-tracker-consultant-fill-in-table-main-container'>
          <div className="compliance-tracker-consultant-fill-in-table-container">
            <table className="compliance-tracker-consultant-fill-in-table">
              <colgroup>
                <col style={{width: "100px"}}/>{/*Column -  View */}
                <col style={{width: "150px"}}/>{/*Column -  Submission Status */}
                <col style={{width: "150px"}}/>{/*Column -  Actions*/}
                <col style={{width: "250px"}}/>{/*Column -  Template Name */}
                <col style={{width: "250px"}}/>{/*Column -  Template Reference */}
                <col style={{width: "100px"}}/>{/*Column -  User Score */}
                <col style={{width: "100px"}}/>{/*Column -  Template Score */}
                <col style={{width: "100px"}}/>{/*Column -  Percentage */}
                <col style={{width: "150px"}}/>{/*Column -  Template Frequency */}
                <col style={{width: "150px"}}/>{/*Column -  Template Frequency */}
                <col style={{width: "150px"}}/>{/*Column -  QA Status */}
                <col style={{width: "200px"}}/>{/*Column -  QA Status Comment */}
              </colgroup>
              
              <thead>
                <tr>
                  <th>View</th>
                  <th>Status</th>
                  <th>QA Actions</th>
                  <th>Template Name</th>
                  <th>Template Reference</th>
                  <th>User Score</th>
                  <th>Template Score</th>
                  <th>Percentage</th>
                  <th>File Frequency</th>
                  <th>File Expiry Date</th>
                  <th>Validity</th>
                  <th>QA Status</th>
                  <th>QA Status Comment</th>
                </tr>
              </thead>

              <tbody>
                {relatedTemplateDocuments.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="no-data-message">
                      <i>No related template documents found.</i>
                    </td>
                  </tr>
                ) : (
                  relatedTemplateDocuments.map((template, index) => {
                    return (
                      <tr key={index}>
                        {/* Column - View  */}
                        <td>
  {template.template_submission_status === 'Saved' || template.template_submission_status === 'Completed' ? (
    <FaRegFolder 
      className='template-save-icon'
      onClick={() => handleViewTemplateDocument(template.compliance_template_submission_id)} 
      style={{ cursor: 'pointer' }}
    />
  ) : 
    <i>No Document Uploaded</i>
  }
</td>

                        {/* Column - Status */}
                        <td>
                          {template.template_submission_status === 'Saved' ? (
                            <div className="saved-status">
                              <IoIosCloudDone /> {template.template_submission_status}
                            </div>
                          ) : template.template_submission_status === 'Not Started' ? (
                            <div className="not-saved-status">
                              <MdCancel /> {template.template_submission_status}
                            </div>
                          ) : template.template_submission_status === 'Completed' ? (
                            <div className="completed-status">
                              <AiOutlineFileDone /> {template.template_submission_status}
                            </div>
                          ) : null}
                        </td>

                        {/* Column - Actions */}
                        <td> 
                          {template.file_validity !== 'Valid' && template.file_validity !== 'Expired' ? (
                            <div className='complaince-tracker-employees-medical-upload-main-table-actions-main-container'>
                              {/* Actions - Approve */}
                              <div>
                                <div>
                                  <TiTick 
                                    className='qa-approve-button'
                                    onClick={() => handleApproveClick(template)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <div>
                                  <RxCrossCircled 
                                    className='qa-reject-button'
                                    onClick={() => handleRejectClick(template)}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p>N/A</p>
                            </div>
                          )}
                        </td>
                        
                        <td>{template.template_name}</td>
                        <td>{template.template_reference}</td>
                        <td>{template.template_user_score}</td>
                        <td>{template.template_score}</td>
                        <td>{template.template_user_percentage} %</td>
                        <td>{template.template_frequency}</td>
                        <td>{template.file_expiry_date}</td>
                        <td>{template.file_validity}</td>
                        <td>{template.qa_status}</td>
                        <td>{template.qa_comment || "No comment"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Template Document Viewer Modal */}
      {viewingTemplateDocument && (
        <div 
          className="full-screen-image-overlay" 
          onClick={closeTemplateDocumentViewer}
        >
          <div className="full-screen-document-container">
            <h3>SHE File Document: {viewingTemplateDocument.file_name || 'Document'}</h3>
            {loadingTemplateDocument ? (
              <div className="loading-spinner-container">
                <LiaSpinnerSolid className="lia-spin-solid-icon" />
              </div>
            ) : (
              renderTemplateDocument(viewingTemplateDocument)
            )}

            <div className="document-details">
              <h3>Document Details</h3>
              <p><strong>File Name:</strong> {viewingTemplateDocument.file_name || 'N/A'}</p>
              <p><strong>Expiry Date:</strong> {viewingTemplateDocument.file_expiry_date || 'N/A'}</p>
              <p><strong>QA Status:</strong> {viewingTemplateDocument.qa_status || 'Not Started'}</p>
              <p><strong>QA Comment:</strong> {viewingTemplateDocument.qa_comment || 'No comments'}</p>
              <p><strong>Submitted By:</strong> {`${viewingTemplateDocument.filled_in_by_user_name || ''} ${viewingTemplateDocument.filled_in_by_user_surname || ''}`}</p>
              <p><strong>Date Submitted:</strong> {new Date(viewingTemplateDocument.date_submitted).toLocaleString()}</p>
            </div>
            
            <button 
              className="close-image-modal-btn" 
              onClick={closeTemplateDocumentViewer}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Full Screen Image Modal */}
      {fullScreenImage && (
        <div 
          className="full-screen-image-overlay" 
          onClick={closeFullScreenImage}
        >
          <div className="full-screen-image-container">
            <img 
              src={fullScreenImage} 
              alt="Full Screen Preview" 
              className="full-screen-image"
              onClick={(e) => e.stopPropagation()} 
            />
            <button 
              className="close-image-modal-btn" 
              onClick={closeFullScreenImage}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* ----------------- Toasters - Approve and Reject */}
      {showApproveToaster && (
        <ApproveQAToasterComponent
          onClose={handleApproveClose}
          onSubmit={handleTemplateApproveSubmission}
          documentId={selectedDocumentForQA?.template_id}
        />
      )}

      {showRejectToaster && (
        <RejectQAToasterComponent
          onClose={handleRejectClose}
          onSubmit={handleRejectTemplateSubmission}
          documentId={selectedDocumentForQA?.template_id}
        />
      )}
    </>
  );
};

export default TemplateA;